import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useEffectEvent,
} from 'react';
import type {
    DocumentNode,
    OperationVariables,
    TypedDocumentNode,
} from '@apollo/client';
import { ErrorLike } from '@apollo/client';
import { skipToken, useQuery } from '@apollo/client/react';

export type PageInfoLike = {
    endCursor?: string | null;
    hasNextPage?: boolean | null;
};

export interface IFetchPagesOptions<
    TData,
    TVariables extends OperationVariables,
> {
    pagesToFetch: number;
    pageSize: number;
    baseVariables?: Partial<TVariables>;
    document: DocumentNode | TypedDocumentNode<TData, TVariables>;
    selectPageInfo: (data?: TData) => PageInfoLike | null | undefined;
    skip?: boolean;
    startCursor?: string | null;
    querySettings?: Omit<useQuery.Options<TData, TVariables>, 'variables'>;
    onError?: (error: ErrorLike) => void;
}

/**
 * Fetch a number of pages for cursor-based pagination using a lazy executor.
 */
export const useFetchPages = <TData, TVariables extends OperationVariables>(
    options: IFetchPagesOptions<TData, TVariables>
) => {
    const {
        pagesToFetch,
        pageSize,
        startCursor,
        baseVariables,
        document,
        selectPageInfo,
        querySettings,
        skip,
        onError,
    } = options;

    // Create key for base variables to avoid re-fetching when they haven't changed
    const baseVarsKey = useMemo(
        () => JSON.stringify(baseVariables || {}),
        [baseVariables]
    );

    const [loading, setLoading] = useState(false);
    const [loadingNextPage, setLoadingNextPage] = useState(false);

    const queryVariables = {
        ...(baseVariables as TVariables),
        first: pageSize,
        ...(startCursor ? { after: startCursor } : {}),
    } as TVariables;

    const queryOptions: useQuery.Options<TData, TVariables> = {
        notifyOnNetworkStatusChange: true,
        variables: queryVariables,
        ...querySettings,
    };

    const {
        data,
        fetchMore,
        error,
        loading: initialLoading,
    } = useQuery(document, skip ? skipToken : queryOptions);

    const handleError = useEffectEvent((error: ErrorLike) => {
        onError?.(error);
    });

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error]);

    const currentCursorRef = useRef<string | null>(startCursor ?? null);

    useEffect(() => {
        // Reset cursor if base variables or start cursor change
        currentCursorRef.current = startCursor ?? null;
    }, [baseVarsKey, startCursor]);

    const pageInfo = useMemo(
        () => selectPageInfo(data as TData | undefined),
        [data, selectPageInfo]
    );

    // Whether there is a next page and the cursor to fetch the next page at.
    const canFetchNextPage = !!pageInfo?.hasNextPage && !!pageInfo?.endCursor;

    /**
     * Fetch a page at a given cursor. If the cursor is not provided, the first page is fetched.
     */
    const fetchPage = useCallback(
        async (
            cursor: string | null
        ): Promise<{
            nextCursor: string | null;
            canFetchNextPage: boolean;
        }> => {
            const variables = {
                ...(baseVariables as TVariables),
                first: pageSize,
                ...(cursor ? { after: cursor } : {}),
            } as TVariables;

            try {
                const response = await fetchMore({
                    variables,
                    // Pass fetchPolicy from querySettings to ensure fetchMore uses the same policy
                    ...(querySettings?.fetchPolicy && {
                        fetchPolicy: querySettings.fetchPolicy,
                    }),
                });

                const respData = response.data as TData | undefined;
                const info = selectPageInfo(respData);
                const nextCursor = info?.endCursor ?? null;

                const canFetchNextPage =
                    info?.hasNextPage === true &&
                    !!nextCursor &&
                    cursor !== nextCursor;

                return { nextCursor, canFetchNextPage };
            } catch {
                // Return empty result to stop pagination
                return { nextCursor: null, canFetchNextPage: false };
            }
        },
        [
            baseVarsKey,
            pageSize,
            selectPageInfo,
            fetchMore,
            baseVariables,
            querySettings,
        ]
    );

    // Track whether we've already started fetching additional pages for the current query
    const hasFetchedAdditionalPages = useRef(false);

    // Reset the flag when base variables change (new query)
    useEffect(() => {
        hasFetchedAdditionalPages.current = false;
    }, [baseVarsKey, startCursor]);

    /**
     * Fetch additional pages after the initial query.
     */
    useEffect(() => {
        if (
            loading ||
            skip ||
            pagesToFetch <= 1 ||
            !pageInfo?.hasNextPage ||
            !pageInfo?.endCursor ||
            hasFetchedAdditionalPages.current
        )
            return;

        // Mark that we've started fetching additional pages
        hasFetchedAdditionalPages.current = true;
        setLoading(true);

        // Async function to fetch the pages recursively.
        const fetchPages = async (cursor: string | null, remaining: number) => {
            if (remaining <= 0) return;

            const { nextCursor, canFetchNextPage } = await fetchPage(cursor);

            // Update the shared cursor ref
            currentCursorRef.current = nextCursor;

            if (!canFetchNextPage) return;

            return fetchPages(nextCursor, remaining - 1);
        };

        // Start from the cursor of the initial query and fetch remaining pages
        const firstCursor = pageInfo.endCursor;
        fetchPages(firstCursor, pagesToFetch - 1).finally(() => {
            setLoading(false);
        });
    }, [skip, pagesToFetch, pageInfo?.endCursor, loading, fetchPage]);

    /**
     * Fetch the next page. If there is no next page, nothing happens.
     */
    const fetchNextPage = useCallback(async () => {
        if (loading || skip || !canFetchNextPage) return;

        setLoadingNextPage(true);

        try {
            const { nextCursor } = await fetchPage(pageInfo?.endCursor ?? null);
            currentCursorRef.current = nextCursor;
        } finally {
            setLoadingNextPage(false);
        }
    }, [pageInfo, canFetchNextPage, skip, loading]);

    return {
        data: data as TData | undefined,
        loading: initialLoading || loading,
        fetchNextPage,
        canFetchNextPage,
        loadingNextPage,
    };
};
